import type { Characteristic, Value } from '../schema';

/**
 * Per-Characteristic price contribution. Either a real Value (selection-based
 * Characteristics where the user picked something) or a synthetic "calculated"
 * line (free-entry pricelist tiers, expression/matrix later).
 */
export interface PriceLine {
    characteristic: Characteristic;
    /** Optional Value that drives this line; absent for pricelist / calculated lines. */
    value: Value | undefined;
    /** Numeric contribution in the configurator's base currency. */
    amount: number;
    /** Optional label override (defaults to characteristic title + value title). */
    label?: string;
}

export interface PriceBreakdown {
    /** Net total — sum of every line's amount. */
    total: number;
    /** Currency code (ISO 4217, e.g. `EUR`). Falls back to `EUR` when the
     * component setting doesn't ship one. */
    currency: string;
    /** Whether the configured prices already include tax. Drives the
     * display logic but doesn't change the total. */
    priceIncludesTax: boolean;
    /** Numeric tax rate in percent (e.g. `19` = 19 %). */
    taxRatePercent: number;
    /**
     * Factor from the Component's currency into {@link currency}; `1` when
     * no conversion applies. Lets views convert amounts that are not lines
     * (Value price suffixes) the same way.
     */
    currencyRate: number;
    /** The host's display tax rate the amounts were rebased onto; undefined
     *  when there is no host (admin preview) and amounts keep their basis. */
    storeTaxRate?: number | undefined;
    /** Per-Characteristic lines, in component sort order. Empty lines
     * (amount 0, no Value) are omitted to keep the summary tight. */
    lines: ReadonlyArray<PriceLine>;
}

export const EMPTY_PRICE_BREAKDOWN: PriceBreakdown = Object.freeze({
    total: 0,
    currency: 'EUR',
    priceIncludesTax: true,
    taxRatePercent: 0,
    currencyRate: 1,
    lines: [],
});
